import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IconButton,
  Input,
  Loader,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
} from "@/components/ui";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import {
  obtenerValoresColumna,
  type RepColumnaSel,
} from "../services/reporteria.api";
import {
  construirFormato,
  construirTransformacion,
  formatoVacio,
  parseFormato,
  parseMapaValores,
  type FormatoConfig,
  type FormatoTipo,
  type ParMapa,
} from "../utils/expresiones";

/** Diálogo de FORMATO granular (número/moneda/fecha/texto) · genera la Expresion NCalc (RN-16). */
export function FormatoDialog({
  columna,
  onAplicar,
  onClose,
}: {
  columna: RepColumnaSel;
  onAplicar: (patch: Partial<RepColumnaSel>) => void;
  onClose: () => void;
}) {
  const [cfg, setCfg] = useState<FormatoConfig>(() => parseFormato(columna.formato));
  const set = (patch: Partial<FormatoConfig>) => setCfg((c) => ({ ...c, ...patch }));

  function cambiarTipo(t: FormatoTipo) {
    // Conserva los valores granulares pero cambia el tipo activo.
    setCfg((c) => ({ ...formatoVacio(t), decimales: c.decimales, miles: c.miles, orden: c.orden, caso: c.caso, trim: c.trim }));
  }

  function aplicar() {
    const { expresion, formato } = construirFormato(cfg);
    // Formato y Transformación son mutuamente excluyentes.
    onAplicar({ expresion, formato: formato || null, mapaValores: null });
    onClose();
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Formato · {columna.etiqueta ?? columna.token}</DialogTitle>
        </DialogHeader>
        <Stack gap="sm">
          <Select
            label="Tipo de formato"
            value={cfg.tipo}
            onValueChange={(v) => cambiarTipo(v as FormatoTipo)}
            options={[
              { value: "ninguno", label: "Sin formato" },
              { value: "numero", label: "Número" },
              { value: "moneda", label: "Moneda" },
              { value: "fecha", label: "Fecha" },
              { value: "texto", label: "Texto" },
            ]}
          />

          {(cfg.tipo === "numero" || cfg.tipo === "moneda") && (
            <div className="flex flex-wrap items-end gap-4">
              <div className="w-32">
                <NumberInput
                  label="Decimales"
                  min={0}
                  max={6}
                  value={cfg.decimales}
                  onChange={(v) => set({ decimales: typeof v === "number" ? v : 0 })}
                />
              </div>
              {cfg.tipo === "numero" && (
                <Switch checked={cfg.miles} onCheckedChange={(v) => set({ miles: v })} label="Separador de miles" />
              )}
            </div>
          )}

          {cfg.tipo === "fecha" && (
            <Stack gap="xs">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <Switch checked={cfg.dia} onCheckedChange={(v) => set({ dia: v })} label="Día" />
                <Switch checked={cfg.mes} onCheckedChange={(v) => set({ mes: v })} label="Mes" />
                <Switch checked={cfg.anio} onCheckedChange={(v) => set({ anio: v })} label="Año" />
                <Switch checked={cfg.mesNombre} onCheckedChange={(v) => set({ mesNombre: v })} label="Mes por nombre" />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <Switch checked={cfg.hora} onCheckedChange={(v) => set({ hora: v })} label="Hora" />
                <Switch checked={cfg.min} onCheckedChange={(v) => set({ min: v })} label="Minutos" />
                <Switch checked={cfg.seg} onCheckedChange={(v) => set({ seg: v })} label="Segundos" />
              </div>
              <div className="w-44">
                <Select
                  label="Orden"
                  value={cfg.orden}
                  onValueChange={(v) => set({ orden: v as FormatoConfig["orden"] })}
                  options={[
                    { value: "dmy", label: "Día / Mes / Año" },
                    { value: "ymd", label: "Año - Mes - Día" },
                  ]}
                />
              </div>
            </Stack>
          )}

          {cfg.tipo === "texto" && (
            <div className="flex flex-wrap items-end gap-4">
              <div className="w-44">
                <Select
                  label="Mayúsculas/minúsculas"
                  value={cfg.caso}
                  onValueChange={(v) => set({ caso: v as FormatoConfig["caso"] })}
                  options={[
                    { value: "none", label: "Sin cambio" },
                    { value: "upper", label: "MAYÚSCULAS" },
                    { value: "lower", label: "minúsculas" },
                    { value: "title", label: "Capitalizado" },
                  ]}
                />
              </div>
              <Switch checked={cfg.trim} onCheckedChange={(v) => set({ trim: v })} label="Sin espacios extremos" />
            </div>
          )}

          <Text size="xs" c="dimmed">
            Reemplaza la presentación de la columna sin cambiar el dato original.
          </Text>
        </Stack>
        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={aplicar}>Aplicar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Diálogo de TRANSFORMACIÓN (mapa valor→texto) · genera un if() anidado (RN-16). */
export function TransformarDialog({
  columna,
  onAplicar,
  onClose,
  fetchValores,
  contexto = "user",
}: {
  columna: RepColumnaSel;
  onAplicar: (patch: Partial<RepColumnaSel>) => void;
  onClose: () => void;
  /** Cómo pedir los valores distintos · por defecto el endpoint del usuario; el
   *  builder de predefinidos inyecta uno por-config. */
  fetchValores?: (token: string) => Promise<unknown[]>;
  contexto?: string;
}) {
  const [pares, setPares] = useState<ParMapa[]>(() => {
    const p = parseMapaValores(columna.mapaValores);
    return p.length > 0 ? p : [{ valor: "", texto: "" }];
  });

  // Sugerencia de valores distintos de la columna (RN-22) para prellenar las llaves.
  const valores = useQuery({
    queryKey: [...qk.repValores(columna.token), contexto],
    queryFn: () => (fetchValores ? fetchValores(columna.token) : obtenerValoresColumna(columna.token, 50)),
    staleTime: 5 * 60_000,
    retry: false,
  });

  function set(i: number, patch: Partial<ParMapa>) {
    setPares((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }
  function add() {
    setPares((prev) => [...prev, { valor: "", texto: "" }]);
  }
  function quitar(i: number) {
    setPares((prev) => prev.filter((_, idx) => idx !== i));
  }

  function aplicar() {
    const { expresion, mapaValores } = construirTransformacion(pares);
    onAplicar({ expresion, mapaValores: expresion ? mapaValores : null, formato: null });
    onClose();
  }

  const sugerencias = (valores.data ?? [])
    .filter((v) => v !== null && v !== undefined)
    .map((v) => String(v));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Transformar valores · {columna.etiqueta ?? columna.token}</DialogTitle>
        </DialogHeader>
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            Reemplaza cada valor por un texto legible. Lo que no coincida se muestra tal cual.
          </Text>
          {sugerencias.length > 0 && (
            <datalist id={`rep-valores-${columna.token}`}>
              {sugerencias.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
          {pares.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="relative w-2/5">
                <input
                  list={`rep-valores-${columna.token}`}
                  className={`h-8 w-full rounded-md border border-border bg-background px-2 text-sm${valores.isPending ? " pr-7" : ""}`}
                  placeholder="valor"
                  value={p.valor}
                  onChange={(e) => set(i, { valor: e.currentTarget.value })}
                />
                {/* Mientras llegan los valores sugeridos (datalist) · indicador discreto en el input. */}
                {valores.isPending && (
                  <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center" title="Cargando sugerencias…">
                    <Loader size="xs" aria-label="Cargando sugerencias" />
                  </span>
                )}
              </div>
              <span className="text-muted-foreground">→</span>
              <Input
                className="flex-1"
                placeholder="texto a mostrar"
                value={p.texto}
                onChange={(e) => set(i, { texto: e.currentTarget.value })}
              />
              <IconButton aria-label="Quitar" variant="subtle" onClick={() => quitar(i)}>
                <IconTrash size={16} />
              </IconButton>
            </div>
          ))}
          <Button variant="subtle" size="xs" leftSection={<IconPlus size={14} />} onClick={add}>
            Agregar valor
          </Button>
        </Stack>
        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={aplicar}>Aplicar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
